import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  fetchPacks,
  createPack,
  updatePack,
  deletePack,
  fetchPlans,
} from '../../store/pricingSlice';
import { pricingService, vehicleSettingsService } from '../../services/api';
import type { PricingPack, PricingPlan, VehicleCategory } from '../../types';

const PricingPacks: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { packs, plans, loading, error } = useAppSelector((state) => state.pricing);
  
  const [categories, setCategories] = useState<VehicleCategory[]>([]);
  const [open, setOpen] = useState(false);
  const [editingPack, setEditingPack] = useState<PricingPack | null>(null);
  const [selectedPlanFilter, setSelectedPlanFilter] = useState<number | ''>('');
  const [formData, setFormData] = useState({
    pricing_plan_id: '',
    vehicle_category_id: '',
    name: '',
    description: '',
    franchise_amount: 0,
    default_rate: 0,
    color: '#1976D2',
    is_active: true,
  });

  useEffect(() => {
    dispatch(fetchPacks());
    dispatch(fetchPlans());
    loadCategories();
  }, [dispatch]);

  const loadCategories = async () => {
    try {
      const response = await vehicleSettingsService.getSettings();
      setCategories(response.data.data?.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const filteredPacks = selectedPlanFilter 
    ? packs.filter(p => p.pricing_plan_id === selectedPlanFilter)
    : packs;

  const handleOpen = (pack?: PricingPack) => {
    if (pack) {
      setEditingPack(pack);
      setFormData({
        pricing_plan_id: pack.pricing_plan_id?.toString() || '',
        vehicle_category_id: pack.vehicle_category_id?.toString() || '',
        name: pack.name,
        description: pack.description || '',
        franchise_amount: pack.franchise_amount || 0,
        default_rate: pack.default_rate || 0,
        color: pack.color || '#1976D2',
        is_active: pack.is_active,
      });
    } else {
      setEditingPack(null);
      setFormData({
        pricing_plan_id: selectedPlanFilter ? selectedPlanFilter.toString() : '',
        vehicle_category_id: '',
        name: '',
        description: '',
        franchise_amount: 0,
        default_rate: 0,
        color: '#1976D2',
        is_active: true,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingPack(null);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        ...formData,
        pricing_plan_id: formData.pricing_plan_id ? parseInt(formData.pricing_plan_id) : undefined,
        vehicle_category_id: formData.vehicle_category_id ? parseInt(formData.vehicle_category_id) : undefined,
      };
      
      if (editingPack) {
        await dispatch(updatePack({ id: editingPack.id, data })).unwrap();
      } else {
        await dispatch(createPack(data)).unwrap();
      }
      handleClose();
      dispatch(fetchPacks());
    } catch (err) {
      console.error('Error saving pack:', err);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce pack ?')) {
      try {
        await dispatch(deletePack(id)).unwrap();
        dispatch(fetchPacks());
      } catch (err) {
        console.error('Error deleting pack:', err);
      }
    }
  };

  const handleDuplicate = (pack: PricingPack) => {
    setEditingPack(null);
    setFormData({
      pricing_plan_id: pack.pricing_plan_id?.toString() || '',
      vehicle_category_id: pack.vehicle_category_id?.toString() || '',
      name: `${pack.name} (copie)`,
      description: pack.description || '',
      franchise_amount: pack.franchise_amount || 0,
      default_rate: pack.default_rate || 0,
      color: pack.color || '#1976D2',
      is_active: true,
    });
    setOpen(true);
  };

  const getPlanName = (planId: number | null) => {
    if (!planId) return '-';
    const plan = plans.find(p => p.id === planId);
    return plan?.name || '-';
  };

  const getCategoryName = (categoryId: number | null | undefined) => {
    if (categoryId == null) return '-';
    const category = categories.find(c => c.id === categoryId);
    return category?.name || '-';
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/pricing/plans')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Gestion des Packs
        </Typography>
      </Box>

      {/* Filter + Add Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filtrer par plan</InputLabel>
          <Select
            value={selectedPlanFilter}
            onChange={(e) => setSelectedPlanFilter(e.target.value as number | '')}
            label="Filtrer par plan"
          >
            <MenuItem value="">Tous les plans</MenuItem>
            {plans.map((plan) => (
              <MenuItem key={plan.id} value={plan.id}>{plan.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
          sx={{ bgcolor: '#4CAF50' }}
        >
          Nouveau Pack
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Packs Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#1976D2' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Nom</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Plan</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Catégorie</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Franchise</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Tarif défaut</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Couleur</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Statut</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredPacks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    Aucun pack trouvé
                  </TableCell>
                </TableRow>
              ) : (
                filteredPacks.map((pack) => (
                  <TableRow key={pack.id} hover>
                    <TableCell>
                      <Typography fontWeight="medium">{pack.name}</Typography>
                      {pack.description && (
                        <Typography variant="caption" color="text.secondary">
                          {pack.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{getPlanName(pack.pricing_plan_id)}</TableCell>
                    <TableCell>{getCategoryName(pack.vehicle_category_id)}</TableCell>
                    <TableCell align="center">
                      {pack.franchise_amount ? `${pack.franchise_amount} €` : '-'}
                    </TableCell>
                    <TableCell align="center">
                      {pack.default_rate ? `${pack.default_rate} €/j` : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          bgcolor: pack.color || '#1976D2',
                          borderRadius: 0.5,
                          mx: 'auto',
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={pack.is_active ? 'Actif' : 'Inactif'}
                        size="small"
                        color={pack.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleDuplicate(pack)} title="Dupliquer">
                        <DuplicateIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleOpen(pack)} title="Modifier">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(pack.id)} color="error" title="Supprimer">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pack Form Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          {editingPack ? 'Modifier le Pack' : 'Nouveau Pack'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Nom du pack"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                size="small"
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                size="small"
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Plan tarifaire</InputLabel>
                <Select
                  value={formData.pricing_plan_id}
                  onChange={(e) => setFormData({ ...formData, pricing_plan_id: e.target.value })}
                  label="Plan tarifaire"
                >
                  <MenuItem value="">Aucun</MenuItem>
                  {plans.map((plan) => (
                    <MenuItem key={plan.id} value={plan.id.toString()}>{plan.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Catégorie véhicule</InputLabel>
                <Select
                  value={formData.vehicle_category_id}
                  onChange={(e) => setFormData({ ...formData, vehicle_category_id: e.target.value })}
                  label="Catégorie véhicule"
                >
                  <MenuItem value="">Aucune</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id.toString()}>{cat.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Montant franchise"
                type="number"
                value={formData.franchise_amount}
                onChange={(e) => setFormData({ ...formData, franchise_amount: parseFloat(e.target.value) || 0 })}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Tarif par défaut"
                type="number"
                value={formData.default_rate}
                onChange={(e) => setFormData({ ...formData, default_rate: parseFloat(e.target.value) || 0 })}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: <InputAdornment position="end">€/jour</InputAdornment>,
                }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">Couleur :</Typography>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: formData.color,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: '1px solid #ccc',
                  }}
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'color';
                    input.value = formData.color;
                    input.addEventListener('change', (e) => {
                      setFormData({ ...formData, color: (e.target as HTMLInputElement).value });
                    });
                    input.click();
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
          <Button onClick={handleClose}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData.name}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PricingPacks;
